import { ChangeEvent, useEffect, useState } from 'react'
import useSWR from 'swr'

import { fetchData } from '@/services/contentful/api/getData'
import { storyCollectionQuery } from '@/services/contentful/queries/storiesOverview'
import { StoryCollectionQuery, TabsContentValues } from '@/interfaces/stories'
import { Story } from '@/interfaces/schema'
import useIsRay from './useIsRay'

const useStoryCollection = ({
  storyType,
}: {
  storyType: TabsContentValues
}) => {
  const isRay = useIsRay()
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false)
  const [doFetch, setDoFetch] = useState<boolean>(true)
  // doFetch state is used to trigger SWR fetch - we use this, because the fetch happens on render OR onClick (in case of search)

  const [numToSkip, setNumToSkip] = useState<number>(0)
  const [storiesData, setStoriesData] = useState<Story[] | null>(null)
  const [totalCount, setTotalCount] = useState<number>(0)

  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isErrorSnackbarOpen, setErrorSnackbarOpen] = useState<boolean>(false)
  const handleCloseErrorSnackbar = () => setErrorSnackbarOpen(false)

  const { data, error } = useSWR<StoryCollectionQuery, Error>(
    !doFetch
      ? null
      : storyCollectionQuery(storyType, searchQuery, numToSkip, isRay),
    fetchData
  )

  if (error) {
    setErrorMessage(
      error?.message ?? 'Oops! We kunnen momenteel de artikelen niet laden...'
    )
    setErrorSnackbarOpen(true)
  }

  const handleLoadMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (storiesData?.length === totalCount) return
    setNumToSkip(storiesData?.length ?? 0)
  }

  const handleInputSearchQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setDoFetch(false)
    setSearchQuery(value)
  }

  const handleSubmitSearch = () => {
    setDoFetch(true)
    setIsSearchActive(!!searchQuery)
  }

  const handleChangeStoryType = () => {
    setNumToSkip(0)
  }

  useEffect(() => {
    if (!data || !doFetch) return

    setTotalCount(data?.storyCollection?.total)

    setStoriesData((prevItems) => {
      const newItems = data?.storyCollection?.items ?? []
      if (numToSkip) {
        return [...(prevItems || []), ...newItems]
      } else {
        return newItems
      }
    })
  }, [data, doFetch, numToSkip])

  return {
    searchQuery,
    isSearchActive,
    handleInputSearchQueryChange,
    handleSubmitSearch,
    storiesData,
    errorMessage,
    isErrorSnackbarOpen,
    handleCloseErrorSnackbar,
    handleLoadMore,
    handleChangeStoryType,
    totalCount,
  }
}

export default useStoryCollection
